import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Select,
  Spinner,
  Stack,
  Tag,
} from '@chakra-ui/react';
import { useInvestorAccountModel } from '@common/models/InvestorAccountModel';
import { ExperienceValues } from '@common/models/InvestorAccountModel/types';
import { ExperienceValueMapping } from '@services/appsync/mappings';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';

export const ExperienceForm: React.FC = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>();
  const { investorAccount, updateExperience } = useInvestorAccountModel();
  const survey = investorAccount?.non_accredited_survey_v2 ?? null;

  const form = useForm<ExperienceValues>({
    defaultValues: {
      alternatives: survey?.experience?.experience_alts?.value ?? ExperienceValueMapping.NONE,
      bonds: survey?.experience?.experience_bonds?.value ?? ExperienceValueMapping.NONE,
      realEstate: survey?.experience?.experience_re?.value ?? ExperienceValueMapping.NONE,
      stocks: survey?.experience?.experience_stocks?.value ?? ExperienceValueMapping.NONE,
    },
  });

  const save = form.handleSubmit(async (data) => {
    setIsSaving(true);
    setFormError(null);
    const result = await updateExperience(data);
    if (result.error) {
      form.reset();
      setIsSaving(false);
      setFormError(result.error);
    } else {
      form.reset(data);
      setIsSaving(false);
    }
  });

  const needsAttention =
    !survey?.experience?.experience_alts?.value ||
    !survey?.experience?.experience_bonds?.value ||
    !survey?.experience?.experience_re?.value ||
    !survey?.experience?.experience_stocks?.value;

  return (
    <Grid templateColumns={['1fr 1fr']} gap={25} w="100%">
      <GridItem colSpan={2} display="flex" flexDirection="row" justifyContent="space-between">
        <Stack direction={['column', 'row']} spacing={[2, 5]}>
          <Heading id="experience" fontSize={'21px'}>
            Experience
          </Heading>
          {needsAttention && <Tag colorScheme={'yellow'}>Needs Attention</Tag>}
        </Stack>

        {form.formState.isDirty &&
          (isSaving ? (
            <Spinner color="blue.500" size="md" thickness="4px" />
          ) : (
            <HStack spacing={5}>
              <Button onClick={save}>Save</Button>
              <Button variant="link" onClick={() => form.reset()}>
                Cancel
              </Button>
            </HStack>
          ))}
      </GridItem>
      {formError && (
        <GridItem colSpan={2}>
          <Alert status="error">
            <AlertIcon />
            {formError}
          </Alert>
        </GridItem>
      )}
      <GridItem colSpan={2} borderTopWidth={1} borderTopColor={'gray.50'} />

      <GridItem colSpan={2}>
        <TextBlock fontSize="lg">How would you rate your investment experience?</TextBlock>
      </GridItem>

      {/* Stock Experience */}
      <GridItem colSpan={[2, 1]}>
        <FormControl isDisabled={isSaving} isInvalid={Boolean(form.formState.errors.stocks)}>
          <FormLabel htmlFor="stocks">Stocks</FormLabel>
          <Select
            placeholder="Select option"
            defaultValue={survey?.experience?.experience_stocks?.value ?? ExperienceValueMapping.NONE}
            {...form.register('stocks', {
              validate: (value) => {
                if (!value) {
                  return 'Stock experience is required';
                } else {
                  return true;
                }
              },
            })}>
            <option value={ExperienceValueMapping.NONE}>None</option>
            <option value={ExperienceValueMapping.LIMITED}>Limited</option>
            <option value={ExperienceValueMapping.EXPERIENCED}>Experienced</option>
          </Select>
          <FormErrorMessage>{form.formState.errors.stocks && form.formState.errors.stocks?.message}</FormErrorMessage>
        </FormControl>
      </GridItem>

      {/* Bonds Experience */}
      <GridItem colSpan={[2, 1]}>
        <FormControl isDisabled={isSaving} isInvalid={Boolean(form.formState.errors.bonds)}>
          <FormLabel htmlFor="stocks">Bonds</FormLabel>
          <Select
            placeholder="Select option"
            defaultValue={survey?.experience?.experience_bonds?.value ?? ExperienceValueMapping.NONE}
            {...form.register('bonds', {
              validate: (value) => {
                if (!value) {
                  return 'Bond experience is required';
                } else {
                  return true;
                }
              },
            })}>
            <option value={ExperienceValueMapping.NONE}>None</option>
            <option value={ExperienceValueMapping.LIMITED}>Limited</option>
            <option value={ExperienceValueMapping.EXPERIENCED}>Experienced</option>
          </Select>
          <FormErrorMessage>{form.formState.errors.bonds && form.formState.errors.bonds?.message}</FormErrorMessage>
        </FormControl>
      </GridItem>

      {/* Real Estate Experience */}
      <GridItem colSpan={[2, 1]} justifyContent="center">
        <FormControl isDisabled={isSaving} isInvalid={Boolean(form.formState.errors.realEstate)}>
          <FormLabel htmlFor="realEstate">Real Estate</FormLabel>

          <Select
            placeholder="Select option"
            defaultValue={survey?.experience?.experience_re?.value ?? ExperienceValueMapping.NONE}
            {...form.register('realEstate', {
              validate: (value) => {
                if (!value) {
                  return 'Real Estate experience is required';
                } else {
                  return true;
                }
              },
            })}>
            <option value={ExperienceValueMapping.NONE}>None</option>
            <option value={ExperienceValueMapping.LIMITED}>Limited</option>
            <option value={ExperienceValueMapping.EXPERIENCED}>Experienced</option>
          </Select>
          <FormErrorMessage>
            {form.formState.errors.realEstate && form.formState.errors.realEstate?.message}
          </FormErrorMessage>
        </FormControl>
      </GridItem>

      {/* Alternatives Experience */}
      <GridItem colSpan={[2, 1]} justifyContent="center">
        <FormControl isDisabled={isSaving} isInvalid={Boolean(form.formState.errors.alternatives)}>
          <FormLabel htmlFor="alternatives">Alternatives</FormLabel>
          <Select
            placeholder="Select option"
            defaultValue={survey?.experience?.experience_alts?.value ?? ExperienceValueMapping.NONE}
            {...form.register('alternatives', {
              validate: (value) => {
                if (!value) {
                  return 'Alternative investment experience is required';
                } else {
                  return true;
                }
              },
            })}>
            <option value={ExperienceValueMapping.NONE}>None</option>
            <option value={ExperienceValueMapping.LIMITED}>Limited</option>
            <option value={ExperienceValueMapping.EXPERIENCED}>Experienced</option>
          </Select>
          <FormErrorMessage>
            {form.formState.errors.alternatives && form.formState.errors.alternatives?.message}
          </FormErrorMessage>
        </FormControl>
      </GridItem>
    </Grid>
  );
};
